import { useFlags } from './useFlags.js';
import type {
  UseMdmPlatformsOptions,
  UseMdmPlatformsResult,
} from './useMdmPlatforms.types.js';
import { useMdmPlatformsOld } from './useMdmPlatformsOld.js';
import { useMdmPlatformsV2 } from './useMdmPlatformsV2.js';

/**
 * Load MDM `platform_v2` metadata for a company and context.
 *
 * This facade switches between the legacy platform loader and the MDM platform
 * relations-backed loader using the `mdmPlatformsV2` feature flag.
 *
 * @param options Hook options.
 * @param options.companyId Company id used to fetch MDM metadata. The metadata
 * query is disabled until this value is provided.
 * @param options.context Metadata context to fetch. Defaults to `integration`.
 * @param options.onlyOmni When true, returns Omni-compatible platforms. The
 * legacy implementation uses the static Omni platform strategy, while the V2
 * implementation returns platforms related to activation-connected, ad-server,
 * or analytics platform types.
 * @returns Platform options, a lookup keyed by platform `value`, and a combined
 * loading state for the required metadata queries.
 */
export const useMdmPlatforms = (
  options: UseMdmPlatformsOptions,
): UseMdmPlatformsResult => {
  const ff = useFlags('@mint/arm', ['mdmPlatformsV2']);
  const isMdmPlatformsV2Enabled = ff.mdmPlatformsV2.enabled;
  const disabledOptions: UseMdmPlatformsOptions = {
    ...options,
    companyId: undefined,
    onlyOmni: false,
  };
  const mdmPlatformsV2 = useMdmPlatformsV2(
    isMdmPlatformsV2Enabled ? options : disabledOptions,
  );
  const mdmPlatformsOld = useMdmPlatformsOld(
    isMdmPlatformsV2Enabled ? disabledOptions : options,
  );

  return isMdmPlatformsV2Enabled ? mdmPlatformsV2 : mdmPlatformsOld;
};

export type {
  UseMdmPlatformsOptions,
  UseMdmPlatformsResult,
} from './useMdmPlatforms.types.js';
